import {
  TRAINABILITY_MODE_KEYS,
  getTrainabilityLevelColor,
  getTrainabilityLevelI18nKey,
  getTrainabilityLevelHex,
  getTrainabilityLevelTextColor,
  normalizeTrainabilityLevel,
  TRAINABILITY_NEUTRAL_COLOR,
  TRAINABILITY_NEUTRAL_HEX,
} from "./TrainabilityLevels";

export const getTrainabilityScore10 = (raw) => {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === "string" && raw.trim() === "") return null;

  const n = Number(raw);
  if (!Number.isFinite(n)) return null;

  return Math.max(0, Math.min(10, n));
};

export const hasTrainability = (raw) => {
  const score = getTrainabilityScore10(raw);
  return Number.isFinite(score) && score > 0;
};

export const hasTrainabilityDisplay = (score, status) => {
  if (hasTrainability(score)) return true;
  return Boolean(normalizeTrainabilityLevel(status));
};

export const formatTrainabilityScore = (raw, { withUnit = true } = {}) => {
  const score = getTrainabilityScore10(raw);
  if (!Number.isFinite(score)) return null;
  const formatted = Number(score.toFixed(1));
  return withUnit ? `${formatted} / 10` : String(formatted);
};

export const getTrainabilityLevelLabel = (level, t) => {
  const key = getTrainabilityLevelI18nKey(level);
  return key && typeof t === "function" ? t(key) : null;
};

export const unwrapTrainabilityLevelDict = (raw, modeKey = "9030") => {
  if (!raw || typeof raw !== "object") return null;
  const nested = raw[modeKey];
  if (nested && typeof nested === "object") return nested;
  if (Object.keys(raw).some((key) => TRAINABILITY_MODE_KEYS.includes(key))) return null;
  return raw;
};

export const resolveTrainabilityLevel = (
  trainabilityLevelObj,
  { group = "global", side, modeKey = "9030" } = {}
) => {
  const dict = unwrapTrainabilityLevelDict(trainabilityLevelObj, modeKey);
  if (!dict) return null;

  if (group === "global") {
    return normalizeTrainabilityLevel(dict.global);
  }

  const muscleStatus = dict[group];
  if (!muscleStatus || typeof muscleStatus !== "object") return null;
  if (!side) return null;
  return normalizeTrainabilityLevel(muscleStatus[side]);
};

export const resolveGlobalTrainabilityLevel = (trainabilityLevelObj, modeKey = "9030") =>
  resolveTrainabilityLevel(trainabilityLevelObj, { group: "global", modeKey });

export const inferTrainabilityLevelFromScore = (score) => {
  const value = getTrainabilityScore10(score);
  if (!Number.isFinite(value)) return null;
  if (value < 3) return "underload";
  if (value < 5) return "suboptimal";
  if (value < 7) return "optimal";
  if (value < 9) return "supraoptimal";
  return "overload";
};

export const resolveTrainabilityVisualLevel = ({ level, status, score } = {}) => {
  const explicit = normalizeTrainabilityLevel(level ?? status);
  if (explicit) return explicit;
  return inferTrainabilityLevelFromScore(score);
};

export const getTrainabilityDisplayColor = ({
  level,
  status,
  score,
  forText = false,
  palette = "legacy",
} = {}) => {
  const resolved = resolveTrainabilityVisualLevel({ level, status, score });
  if (!resolved) return TRAINABILITY_NEUTRAL_COLOR;
  return forText
    ? getTrainabilityLevelTextColor(resolved, palette)
    : getTrainabilityLevelColor(resolved, palette);
};

export const getTrainabilityThreeColorHex = (status, palette = "legacy") => {
  const level = normalizeTrainabilityLevel(status);
  return level ? getTrainabilityLevelHex(level, palette) : TRAINABILITY_NEUTRAL_HEX;
};

export const getTrainabilityDisplayLabel = ({
  trainabilityLevelObj,
  score,
  t,
  modeKey = "9030",
} = {}) => {
  const level = resolveTrainabilityVisualLevel({
    level: resolveGlobalTrainabilityLevel(trainabilityLevelObj, modeKey),
    score,
  });
  const label = level ? getTrainabilityLevelLabel(level, t) : null;
  return {
    level,
    label: label ?? level ?? null,
  };
};

export const getTrainabilityTextStyle = ({
  level,
  status,
  score,
  isDarkMode = false,
  palette = "legacy",
} = {}) => {
  const resolved = resolveTrainabilityVisualLevel({ level, status, score });
  if (!resolved) {
    return {
      className: isDarkMode ? "text-gray-300" : "text-gray-500",
      color: null,
    };
  }

  return {
    className: "font-bold",
    color: getTrainabilityLevelTextColor(resolved, palette),
  };
};
